import { Alert, BrandLogo, Button, Card, Field } from '@catering/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { LogIn } from 'lucide-react';
import { type FormEvent, type JSX, useState } from 'react';

import { apiRequest, ApiError } from '../api/client';
import { resolveRoleHome } from '../auth/resolve-role-home';
import { useSessionStore } from '../auth/session-store';
import { useLogin } from '../auth/use-login';
import { type CurrentUser } from '../auth/use-current-user';

const PORTAL_URL: string =
  (import.meta.env.VITE_PORTAL_URL as string | undefined) ?? 'http://localhost:5174';

/**
 * The second factor field only appears once the server asks for it, which it
 * does by answering `totpRequired`. Showing it to everyone would be asking a
 * question most people cannot answer.
 */
export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const { reset, activated } = useSearch({ from: '/login' });
  const queryClient = useQueryClient();
  const login = useLogin();
  const [totpRequired, setTotpRequired] = useState(false);
  const authError = useSessionStore((state) => state.authError);

  const loginErrorMessage = login.isError ? describeLoginError(login.error, totpRequired) : null;

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);

    try {
      await login.mutateAsync({
        email: readText(form, 'email'),
        password: readText(form, 'password'),
        ...(totpRequired ? { totpCode: readText(form, 'totpCode') } : {}),
      });

      const profile = await apiRequest<CurrentUser>('/auth/me');
      const currentUser: CurrentUser = { ...profile, permissions: profile.permissions ?? [] };

      // Same key `useCurrentUser` reads: `AppShell` finds a warm cache entry
      // instead of firing a second `/auth/me` right after this one.
      queryClient.setQueryData(['me'], currentUser);

      const destination = resolveRoleHome(currentUser.roleCodes, new Date());

      await navigate({
        to: destination.to,
        ...(destination.search !== undefined ? { search: destination.search } : {}),
      });
    } catch (error) {
      if (error instanceof ApiError && isTotpRequired(error)) {
        setTotpRequired(true);
      }
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 via-white to-slate-50 p-4">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <div className="flex flex-col items-center gap-1">
          <BrandLogo size="lg" />
          <span className="text-sm text-slate-500">Back-office</span>
        </div>

        <Card variant="elevated" className="w-full">
          <form
            onSubmit={(event) => void handleSubmit(event)}
            className="flex flex-col gap-5"
            noValidate
          >
            <header>
              <h1 className="text-lg font-semibold text-slate-900">Iniciar sesión</h1>
              <p className="mt-1 text-sm text-slate-500">Ingresá para continuar.</p>
            </header>

            {reset === 'success' ? (
              <Alert tone="success">Tu contraseña se actualizó. Ingresá con la nueva.</Alert>
            ) : null}

            {activated === 'success' ? (
              <Alert tone="success">Tu cuenta quedó activa. Ingresá con tu correo y tu contraseña.</Alert>
            ) : null}

            {authError !== null ? (
              <Alert tone="warning">
                {authError}{' '}
                <a href={PORTAL_URL} className="font-medium underline">
                  Ir al portal
                </a>
                .
              </Alert>
            ) : null}

            {loginErrorMessage !== null && authError === null ? (
              <Alert tone="danger">{loginErrorMessage}</Alert>
            ) : null}

            <Field
              label="Correo electrónico"
              name="email"
              type="email"
              autoComplete="username"
              required
              // Focused on load: this is the first field of the first screen, and
              // a keyboard user should not have to tab into it.
              autoFocus
            />

            <Field
              label="Contraseña"
              name="password"
              type="password"
              autoComplete="current-password"
              required
            />

            {totpRequired ? (
              <Field
                label="Código de verificación"
                name="totpCode"
                inputMode="numeric"
                autoComplete="one-time-code"
                hint="Seis dígitos de tu aplicación de autenticación"
                required
                autoFocus
              />
            ) : null}

            <Button type="submit" icon={LogIn} loading={login.isPending} className="w-full">
              Ingresar
            </Button>

            <Link
              to="/forgot-password"
              className="text-center text-sm font-medium text-primary-700 underline"
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </form>
        </Card>
      </div>
    </main>
  );
}

/**
 * FormData.get returns a string or a File, and String(file) quietly yields
 * "[object File]". Narrowing here means a malformed field arrives as empty
 * text the server will reject, rather than as nonsense it might accept.
 */
function readText(form: FormData, field: string): string {
  const value = form.get(field);

  return typeof value === 'string' ? value : '';
}

function isTotpRequired(error: ApiError): boolean {
  return (error.body as { totpRequired?: boolean } | null)?.totpRequired === true;
}

/**
 * The "code required" answer is a question, not a failure, so it shows
 * nothing. Once the code field is on screen, a 401 can only mean that code.
 */
function describeLoginError(error: unknown, totpRequired: boolean): string | null {
  if (!(error instanceof ApiError)) {
    return CONNECTION_PROBLEM;
  }
  if (isTotpRequired(error)) {
    return null;
  }
  if (error.status === 403) {
    return 'Tu cuenta está desactivada. Hablá con un administrador.';
  }
  if (error.status >= 500) {
    return CONNECTION_PROBLEM;
  }
  if (totpRequired) {
    return 'Código incorrecto o vencido. Probá con el código actual.';
  }

  return 'No pudimos ingresar con esos datos.';
}

const CONNECTION_PROBLEM = 'Hubo un problema de conexión. Probá de nuevo.';
